import { useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import SyncRoundedIcon from "@mui/icons-material/SyncRounded"
import { LeadRoutingClient, RecentRoutedLead } from "@clients/leadRoutingClient"
import { PROGRAM_LABEL } from "./leadRoutingLabels"
import { relativeTime, ValuesChip } from "./freshness"
import { HubSpotLink } from "./hubspotLink"

// The routed leads exactly as the engine assigned them over a date range: when,
// which rule matched, who got the lead, and which values the decision used. The
// Sync now button re-reads every sampled lead's current HubSpot state (via
// campus), so replay and coverage answer for leads as they are today.
export function IncomingLeads(props: { reloadKey?: number }) {
  const todayIso = new Date().toISOString().slice(0, 10)
  const weekAgoIso = new Date(Date.now() - 7 * 86400_000)
    .toISOString()
    .slice(0, 10)
  const [from, setFrom] = useState(weekAgoIso)
  const [to, setTo] = useState(todayIso)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(15)
  const [leads, setLeads] = useState<RecentRoutedLead[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string>()

  // A narrower range or a new page size resets to the first page so the view
  // never lands on a page that no longer exists.
  useEffect(() => {
    setPage(0)
  }, [from, to, pageSize])

  const load = useCallback(() => {
    if (!from || !to || from > to) return
    LeadRoutingClient.listRecentRoutedLeads(from, to, page, pageSize)
      .then((result) => {
        setLeads(result.rows)
        setTotal(result.total)
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
    // props.reloadKey is intentional: bumping it from the global refresh
    // re-creates load so the effect below refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, page, pageSize, props.reloadKey])

  useEffect(() => {
    load()
  }, [load])

  const resync = useCallback(async () => {
    setSyncing(true)
    setError(undefined)
    try {
      await LeadRoutingClient.resyncLeadSample()
      load()
    } catch {
      setError("HubSpot could not be reached, try again in a minute.")
    } finally {
      setSyncing(false)
    }
  }, [load])

  if (!loaded) {
    return null
  }

  return (
    <Stack sx={{ mb: 3 }} spacing={0.5}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="subtitle1">Leads routed by the API</Typography>
        <TextField
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          inputProps={{ max: to, style: { fontSize: 13 } }}
        />
        <Typography variant="caption" color="text.secondary">
          to
        </Typography>
        <TextField
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          inputProps={{ min: from, max: todayIso, style: { fontSize: 13 } }}
        />
        <Button
          size="small"
          startIcon={<SyncRoundedIcon fontSize="inherit" />}
          onClick={resync}
          disabled={syncing || total === 0}
          sx={{ textTransform: "none", py: 0, minWidth: 0 }}
        >
          {syncing ? "Syncing…" : "Sync from HubSpot"}
        </Button>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>

      {total === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No leads routed by the API in this range.
        </Typography>
      ) : (
        <Table
          size="small"
          sx={{ "& td, & th": { py: 0.5, fontSize: "0.8rem" } }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Routed</TableCell>
              <TableCell>Lead</TableCell>
              <TableCell>Matching rule</TableCell>
              <TableCell>Assigned by</TableCell>
              <TableCell>Consultant</TableCell>
              <TableCell>QTF</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Values used</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.map((lead) => (
              <TableRow key={lead.leadId}>
                <TableCell>{relativeTime(lead.routedAt)}</TableCell>
                <TableCell>{lead.leadName ?? "-"}</TableCell>
                <TableCell>
                  {lead.ruleName ??
                    (lead.source === "fallback" ? "Everyone else" : "-")}
                </TableCell>
                <TableCell>
                  {lead.source === "fallback" &&
                  lead.ruleName &&
                  !lead.ruleIsFallback ? (
                    <Tooltip title="The matching rule had no eligible consultant (off shift, paused or filtered), so the lead went to the least-recently-assigned consultant among everyone on shift.">
                      <Box
                        component="span"
                        sx={{ color: "warning.main", cursor: "help" }}
                      >
                        Round-robin
                      </Box>
                    </Tooltip>
                  ) : lead.source === "fallback" ? (
                    "Round-robin"
                  ) : lead.source === "held" ? (
                    "Held"
                  ) : lead.source === "weighted" ? (
                    "Rule split"
                  ) : (
                    lead.source
                  )}
                </TableCell>
                <TableCell>{lead.consultantName ?? "-"}</TableCell>
                <TableCell>{String(lead.snapshot.qtfScore ?? "-")}</TableCell>
                <TableCell>
                  {PROGRAM_LABEL[String(lead.snapshot.fieldOfStudyInterest)] ??
                    String(lead.snapshot.fieldOfStudyInterest ?? "-")}
                </TableCell>
                <TableCell>
                  <ValuesChip
                    source={
                      lead.snapshot.propertiesSource as string | undefined
                    }
                    syncedAt={
                      lead.snapshot.propertiesSyncedAt as string | undefined
                    }
                  />
                </TableCell>
                <TableCell padding="checkbox">
                  <HubSpotLink email={lead.email} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[15, 25, 50, 100]}
          labelRowsPerPage="Per page"
          sx={{ "& .MuiTablePagination-toolbar": { minHeight: 40, px: 0 } }}
        />
      )}
    </Stack>
  )
}
